<template>
  <div class="maps">
    <div class="mapv3-shell">
      <!-- Reusable MapLibre engine (markers, clustering, route layers) — unchanged. -->
      <Map ref="map" />

      <!-- Left panel: vehicle list — faithful port of the legacy mapv3 fleet list, adapted to
           the front-app theme. Data + selection reuse the map's store and emitter API, so the
           list and the markers always stay in sync. -->
      <aside
        class="mapv3-list"
        :class="{ 'mapv3-list--collapsed': !listOpen }"
        aria-label="Lista pojazdów"
      >
        <div class="mapv3-list__header">
          <div class="mapv3-list__head">
            <div class="mapv3-list__title">
              {{ $t('aside.menu.pages.vehicles') }}
            </div>
            <button
              class="mapv3-panel-close"
              type="button"
              :title="$t('components.map.list.collapse')"
              @click="listOpen = false"
            >
              <i class="bi bi-x-lg" />
            </button>
          </div>

          <div class="mapv3-list__search">
            <i class="bi bi-search" />
            <input
              v-model="search"
              class="mapv3-list__search-input"
              type="text"
              :placeholder="$t('components.map.list.searchPlaceholder')"
              autocomplete="off"
            >
          </div>

          <div class="mapv3-list__filters">
            <button
              class="mapv3-list__filter"
              :class="{ 'mapv3-list__filter--active': listFilter === 'grouped' }"
              type="button"
              @click="listFilter = 'grouped'"
            >
              {{ $t('components.map.list.filters.grouped') }}
            </button>
            <button
              class="mapv3-list__filter"
              :class="{ 'mapv3-list__filter--active': listFilter === 'all' }"
              type="button"
              @click="listFilter = 'all'"
            >
              {{ $t('components.map.list.filters.all') }} · {{ allCount }}
            </button>
            <button
              class="mapv3-list__filter mapv3-list__filter--alert"
              :class="{ 'mapv3-list__filter--active': listFilter === 'alert' }"
              type="button"
              @click="listFilter = 'alert'"
            >
              {{ $t('components.map.list.filters.alert') }} · {{ alertCount }}
            </button>
            <span
              class="mapv3-list__visible"
              :title="$t('components.map.list.visible')"
            >{{ visibleCount }}</span>
          </div>
        </div>

        <div class="mapv3-list__scroll">
          <div
            v-if="!hasRows"
            class="mapv3-list__empty"
          >
            {{ vehicles.length ? $t('components.map.list.empty') : $t('components.map.list.loading') }}
          </div>

          <section
            v-for="block in renderBlocks"
            :key="block.key"
            class="mapv3-list__group"
            :class="{ 'is-collapsed': block.name && collapsedGroups[block.key] }"
          >
            <button
              v-if="block.name"
              class="mapv3-list__group-header"
              type="button"
              @click="toggleGroup(block.key)"
            >
              <i class="bi bi-chevron-down mapv3-list__group-chevron" />
              <span class="mapv3-list__group-name">{{ block.name }}</span>
              <span class="mapv3-list__group-count">{{ block.rows.length }}</span>
            </button>

            <div
              v-show="!block.name || !collapsedGroups[block.key]"
              class="mapv3-list__group-rows"
            >
              <div
                v-for="v in block.rows"
                :key="v.imei"
                class="mapv3-list__row"
                :class="{
                  'mapv3-list__row--selected': v.imei === selectedImei,
                  'mapv3-list__row--alert': v._alerts.length
                }"
                @click="selectVehicle(v)"
              >
                <span
                  class="mapv3-list__icon"
                  :style="{ background: v._palette.tint, color: v._palette.color }"
                >
                  <i class="bi bi-truck" />
                </span>
                <div class="mapv3-list__row-main">
                  <div class="mapv3-list__plate">
                    {{ v._plate }}
                  </div>
                  <div class="mapv3-list__driver">
                    {{ v._driver }}
                  </div>
                  <div
                    v-if="v._alerts.length"
                    class="mapv3-list__alerts"
                  >
                    <div
                      v-for="(a, i) in v._alerts"
                      :key="i"
                      class="mapv3-list__alert"
                    >
                      {{ a }}
                    </div>
                  </div>
                </div>
                <div class="mapv3-list__right">
                  <div
                    class="mapv3-list__speed"
                    :style="{ color: v._palette.color }"
                  >
                    {{ v._speedTxt }}
                  </div>
                  <div
                    class="mapv3-list__status"
                    :style="{ color: v._status === 'alert' ? alertColor : '' }"
                  >
                    {{ statusLabel(v._status) }}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </aside>

      <!-- Reopen handle (shown when the list is collapsed). -->
      <button
        v-if="!listOpen"
        class="mapv3-panel-handle"
        type="button"
        @click="listOpen = true"
      >
        <i class="bi bi-list" /> {{ $t('aside.menu.pages.vehicles') }}
      </button>

      <!-- Right info panel — opens on vehicle selection (list row or map marker). -->
      <Mapv3InfoPanel
        v-if="selectedVehicle"
        :vehicle="selectedVehicle"
        @close="closePanel"
      />

      <!-- Map controls cluster (bottom-right, mapv3 model): route planner, zoom, center-fleet, settings. -->
      <div class="mapv3-controls">
        <button
          class="mapv3-controls__center"
          :class="{ 'mapv3-controls__center--active': routePlannerActive }"
          type="button"
          :title="$t('components.map.routePlanning.title')"
          @click="toggleRoutePlanner"
        >
          <i class="bi bi-sign-turn-right" />
        </button>

        <div class="mapv3-controls__zoom">
          <button
            class="mapv3-controls__btn mapv3-controls__btn--top"
            type="button"
            :title="$t('components.map.controls.zoomIn')"
            @click="zoomIn"
          >
            <i class="bi bi-plus-lg" />
          </button>
          <button
            class="mapv3-controls__btn"
            type="button"
            :title="$t('components.map.controls.zoomOut')"
            @click="zoomOut"
          >
            <i class="bi bi-dash-lg" />
          </button>
        </div>

        <button
          class="mapv3-controls__center"
          type="button"
          :title="$t('components.map.controls.fitFleet')"
          @click="fitFleet"
        >
          <i class="bi bi-fullscreen" />
        </button>

        <div
          class="mapv3-map-controls"
          :class="{ 'is-open': controlsOpen }"
        >
          <button
            class="mapv3-map-controls__toggle"
            :class="{ 'mapv3-map-controls__toggle--active': controlsOpen }"
            type="button"
            :title="$t('components.map.controls.settings')"
            @click="controlsOpen = !controlsOpen"
          >
            <i class="bi bi-sliders" />
          </button>
          <div class="mapv3-map-controls__menu">
            <div class="mapv3-map-controls__title">
              {{ $t('components.map.controls.baseMap') }}
            </div>
            <div class="mapv3-map-controls__styles">
              <button
                v-for="p in availableProviders"
                :key="p.id"
                class="mapv3-map-controls__style"
                :class="{ 'is-active': selectedProvider === p.id }"
                type="button"
                @click="selectProvider(p.id)"
              >
                <span
                  class="mapv3-map-controls__style-preview"
                  :style="{ background: p.preview }"
                />
                <span>{{ p.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch } from 'vue'
import Map from '@/components/content/Map.vue'
import Mapv3InfoPanel from '@/components/content/Mapv3InfoPanel.vue'
import { useI18n } from 'vue-i18n'
import { vehiclesFromMapStore } from '@/stores/vehiclesFromMapStore'
import { getVehicleName } from '@/services/map/vehicle-marker'
import {
  STATUS_PALETTE,
  collectVehicleAlerts,
  deriveStatus,
  speedText,
  VehicleStatus
} from '@/services/map/vehicle-status'
import { getAssetGroups, getAssetGroup } from '@/crud/asset-group/getters'
import { getParameterFromToken } from '@/crud/getters'
import { resolveProvider, isProviderAvailable, MapProvider } from '@/services/map/map-style'
import { canAccessAssetModule } from '@/utils/tools'

interface FleetGroup {
  id: number | string
  name: string
  ids: Set<string>
}

interface ListBlock {
  key: string
  name: string | null
  rows: Array<any>
}

export default defineComponent({
  name: 'MapView',
  components: { Map, Mapv3InfoPanel },
  setup(props, context) {
    const i18n = useI18n()
    context.emit('pageName', i18n.t('aside.menu.pages.map'))

    watch(() => i18n.locale.value, () => {
      context.emit('pageName', i18n.t('aside.menu.pages.map'))
    })
  },
  data() {
    return {
      vehicles: [] as Array<any>,
      groups: [] as Array<FleetGroup>,
      listOpen: true,
      listFilter: 'grouped' as 'grouped' | 'all' | 'alert',
      search: '',
      collapsedGroups: {} as Record<string, boolean>,
      selectedImei: null as any,
      selectedVehicle: null as any,
      alertColor: STATUS_PALETTE.alert.color,
      controlsOpen: false,
      routePlannerActive: false,
      selectedProvider: 'OFM' as MapProvider,
      providers: [
        { id: 'OFM', label: 'OpenFreeMap', preview: '#eef0f2' },
        { id: 'OSM', label: 'OpenStreetMap', preview: '#cfe3b8' },
        { id: 'HERE', label: 'HERE', preview: '#4a6fa5' },
        { id: 'PTV', label: 'PTV', preview: '#1a9e8f' },
        { id: 'WEBFLEET', label: 'WebFleet', preview: '#e04e2a' },
        { id: 'GOOGLE', label: 'Google', preview: '#aecbf0' }
      ] as Array<{ id: MapProvider; label: string; preview: string }>
    }
  },
  computed: {
    // Decorate each vehicle once with its status/alerts/labels (same logic as the legacy list).
    decorated(): Array<any> {
      const t = (key: string, named?: Record<string, unknown>) => this.$t(key, named ?? {}) as string
      return this.vehicles.map((v: any) => {
        const alerts = collectVehicleAlerts(v, t)
        const status = deriveStatus(v, alerts)
        const plate = getVehicleName(v) || String(v.imei ?? '—')
        const driver = v.driver_full_name || v.driverFullName || v.driver || '—'
        const speedTxt = speedText(v)
        const haystack = this.norm([plate, driver, this.statusLabel(status), alerts.join(' '), v.imei ?? ''].join(' '))
        return {
          ...v,
          _alerts: alerts,
          _status: status,
          _palette: STATUS_PALETTE[status],
          _plate: plate,
          _driver: driver,
          _speedTxt: speedTxt,
          _haystack: haystack
        }
      })
    },
    allCount(): number {
      return this.decorated.length
    },
    alertCount(): number {
      return this.decorated.filter((v: any) => v._status === 'alert').length
    },
    visibleCount(): number {
      return this.decorated.filter((v: any) => v.visible).length
    },
    searchFiltered(): Array<any> {
      const q = this.norm(this.search)
      if (!q) return this.decorated
      return this.decorated.filter((v: any) => v._haystack.indexOf(q) !== -1)
    },
    // "Grupy" mode: one block per asset group (by vehicle membership) + a "Bez grupy" block.
    groupedBlocks(): Array<ListBlock> {
      const list = this.searchFiltered
      const byId: Record<string, any> = {}
      list.forEach((v: any) => {
        if (v.id !== undefined && v.id !== null) byId[String(v.id)] = v
      })
      const shown: Record<string, boolean> = {}
      const blocks: Array<ListBlock> = []
      this.groups.forEach((g: FleetGroup) => {
        const rows: Array<any> = []
        g.ids.forEach((id: string) => {
          const v = byId[id]
          if (v) {
            rows.push(v)
            shown[id] = true
          }
        })
        if (rows.length) blocks.push({ key: 'g-' + g.id, name: g.name, rows })
      })
      const ungrouped = list.filter((v: any) => v.id === undefined || v.id === null || !shown[String(v.id)])
      if (ungrouped.length) {
        blocks.push({ key: 'ungrouped', name: this.$t('components.map.list.ungrouped') as string, rows: ungrouped })
      }
      return blocks
    },
    renderBlocks(): Array<ListBlock> {
      if (this.listFilter === 'grouped') return this.groupedBlocks
      const rows = this.listFilter === 'alert'
        ? this.searchFiltered.filter((v: any) => v._status === 'alert')
        : this.searchFiltered
      return rows.length ? [{ key: this.listFilter, name: null, rows }] : []
    },
    hasRows(): boolean {
      return this.renderBlocks.some((b: ListBlock) => b.rows.length > 0)
    },
    // Only base maps whose API key is configured; a keyless provider (HERE/PTV/…) would just
    // return 401 and render a blank map, so it must not appear in the switcher.
    availableProviders(): Array<{ id: MapProvider; label: string; preview: string }> {
      return this.providers.filter((p) => isProviderAvailable(p.id))
    }
  },
  mounted() {
    const self: any = this
    document.body.classList.add('map')

    // Seed from the store (map may have fetched already), then track live updates the map
    // emits on every refresh — one data source shared with the markers.
    this.vehicles = Array.isArray(vehiclesFromMapStore.vehiclesFromMap)
      ? vehiclesFromMapStore.vehiclesFromMap
      : []

    self.mapv3Handlers = {
      vehiclesFromMap: (data: any) => {
        this.vehicles = Array.isArray(data) ? data : []
      },
      closeSidebar: () => {
        this.selectedImei = null
        this.selectedVehicle = null
      },
      mapVehicleSelected: (obj: any) => {
        this.openInfo(obj)
      }
    }
    self.emitter.on('vehiclesFromMap', self.mapv3Handlers.vehiclesFromMap)
    self.emitter.on('closeSidebar', self.mapv3Handlers.closeSidebar)
    self.emitter.on('mapVehicleSelected', self.mapv3Handlers.mapVehicleSelected)

    this.loadGroups()

    // Highlight the base map that the token/default actually selected.
    getParameterFromToken('MAP_TYPE')
      .then((res: any) => { this.selectedProvider = resolveProvider(res) })
      .catch(() => { /* keep default */ })
  },
  unmounted() {
    const self: any = this
    document.body.classList.remove('map')
    if (self.mapv3Handlers) {
      self.emitter.off('vehiclesFromMap', self.mapv3Handlers.vehiclesFromMap)
      self.emitter.off('closeSidebar', self.mapv3Handlers.closeSidebar)
      self.emitter.off('mapVehicleSelected', self.mapv3Handlers.mapVehicleSelected)
    }
  },
  methods: {
    // Accent-insensitive normalization for search (matches legacy behaviour).
    norm(value: any): string {
      let s = String(value ?? '').toLowerCase()
      if (typeof s.normalize === 'function') {
        s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      }
      return s.trim()
    },
    statusLabel(status: VehicleStatus): string {
      return this.$t('components.map.list.status.' + status) as string
    },
    toggleGroup(key: string): void {
      this.collapsedGroups[key] = !this.collapsedGroups[key]
    },
    // Load asset-group vehicle membership once (rarely changes); regrouping on each position
    // refresh happens client-side against this cache. Vehicles only — assets are untouched.
    async loadGroups(): Promise<void> {
      // The asset-group endpoints require asset-module access; skip the fetch for users without
      // it (would 403). Grouped mode then simply shows everything under "Bez grupy".
      if (!canAccessAssetModule()) {
        this.groups = []
        return
      }
      try {
        const res: any = await getAssetGroups({ take: 1000 })
        const raw = res?.data?.data?.result ?? res?.data?.result ?? (Array.isArray(res?.data) ? res.data : [])
        const groups = Array.isArray(raw) ? raw : []
        this.groups = await Promise.all(
          groups
            .filter((g: any) => Number(g?.vehiclesCount ?? 0) > 0 && g?.id != null)
            .map(async (g: any): Promise<FleetGroup> => {
              try {
                const detail: any = await getAssetGroup(g.id)
                const vs = detail?.data?.vehicles ?? detail?.data?.data?.vehicles ?? []
                const ids = new Set<string>((Array.isArray(vs) ? vs : []).map((x: any) => String(x.id)))
                return { id: g.id, name: g.name ?? '', ids }
              } catch (_) {
                return { id: g.id, name: g.name ?? '', ids: new Set<string>() }
              }
            })
        )
      } catch (_) {
        this.groups = []
      }
    },
    selectVehicle(v: any): void {
      if (!v?.imei) return
      const self: any = this
      // Isolate the vehicle on the map (same flow markers use), then open the info panel.
      self.emitter.emit('hideOtherMarkers', v.imei)
      this.openInfo(v)
    },
    // Open the right info panel for a vehicle (list row or map marker). Always resolves to the
    // decorated vehicle so the panel has status/plate; raw marker payloads are looked up by imei.
    openInfo(v: any): void {
      if (!v?.imei) return
      const dec = this.decorated.find((d: any) => String(d.imei) === String(v.imei)) || v
      this.selectedImei = dec.imei
      this.selectedVehicle = dec
      // Centre the map on the selected vehicle's current point.
      const map: any = this.$refs.map
      if (map && typeof map.centerOn === 'function' && dec.lat != null && dec.lng != null) {
        map.centerOn([Number(dec.lng), Number(dec.lat)])
      }
    },
    closePanel(): void {
      const self: any = this
      this.selectedVehicle = null
      this.selectedImei = null
      self.emitter.emit('closeSidebar')
    },
    fitFleet(): void {
      const map: any = this.$refs.map
      if (map && typeof map.fitFleet === 'function') map.fitFleet()
    },
    zoomIn(): void {
      const map: any = this.$refs.map
      if (map && typeof map.zoomIn === 'function') map.zoomIn()
    },
    zoomOut(): void {
      const map: any = this.$refs.map
      if (map && typeof map.zoomOut === 'function') map.zoomOut()
    },
    // Route planner lives in the Map component; toggle it and mirror its active flag for styling.
    toggleRoutePlanner(): void {
      const map: any = this.$refs.map
      if (map && typeof map.toggleRoutePlanner === 'function') {
        map.toggleRoutePlanner()
        this.routePlannerActive = !!map.routePlanner?.active
      }
    },
    selectProvider(p: MapProvider): void {
      const map: any = this.$refs.map
      if (map && typeof map.changeProvider === 'function') map.changeProvider(p)
      this.selectedProvider = p
      this.controlsOpen = false
    }
  }
})
</script>

<style lang="scss" src="@/assets/scss/mapv3.scss"></style>
